import { useCallback, useRef, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, FileType } from "build-ts/common";
import {
  ArtifactKind,
  ArtifactMap,
  getArtifactHeaderName,
  getArtifactMapKey,
  getArtifactType,
} from "../utils/artifactUtils";
import { ARTIFACT_TAG_TYPE } from "modules/chat/utils/chatUtils";

/** Returns (and lazily creates) the inner map for a given kind. */
function getOrCreate(map: ArtifactMap, key: ArtifactKind): Map<string, ArtifactProto> {
  let inner = map.get(key);
  if (!inner) {
    inner = new Map();
    map.set(key, inner);
  }
  return inner;
}

/**
 * Creates a shallow clone of the outer map with each inner map also cloned.
 * Used inside setArtifactMap updaters to produce a new reference for React.
 */
function cloneArtifactMap(map: ArtifactMap): ArtifactMap {
  const clone: ArtifactMap = new Map();
  Array.from(map.entries()).forEach(([key, inner]) => {
    clone.set(key, new Map(inner));
  });
  return clone;
}

/**
 * Writes all entries for an artifact into the nested map.
 *
 * Bucket assignments:
 *   DATAFRAME_ANALYSIS  → header name into "code"; each pandas_df → "dataframe";
 *                         each chart → "chart"; each report → "report".
 *   KG_DATAFRAME        → header name into "dataframe".
 *   SCENARIO_JOB        → header name into "workflow".
 *   ANALYSIS_OUTPUT     → header name into "workflow-run".
 *
 *   FILE_ARTIFACT        → CSV: header id into "file"; CHART → "chart"; DOCX → "report".
 *   WORKFLOW_RUN         → header name into "workflow-run-detail"; each nested FileArtifact
 *                         name → "dataframe"/"chart"/"report" pointing at the parent run.
 *   WORKFLOW_RUN_SEARCH  → header name into "workflow-run-search"; each search-result run's
 *                         nested FileArtifact names indexed the same way (parent = synthetic run).
 */
function indexFileArtifact(artifact: ArtifactProto, map: ArtifactMap): void {
  const fileType = artifact.file_artifact?.file_type;
  const name = getArtifactHeaderName(artifact);
  if (fileType === FileType.CHART_SPEC_JSON) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.chart).set(name, artifact);
    return;
  }
  if (fileType === FileType.DOCX) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.report).set(name, artifact);
    return;
  }
  const key = getArtifactMapKey(artifact);
  if (key) getOrCreate(map, ARTIFACT_TAG_TYPE.file).set(key, artifact);
}

/**
 * Index nested FileArtifact names on a workflow run → parent WORKFLOW_RUN proto.
 * Mirrors DATAFRAME_ANALYSIS child indexing (chart/dataframe/report buckets → parent).
 */
function indexWorkflowRunNestedNames(
  parent: ArtifactProto,
  nestedArtifacts: ArtifactProto[] | undefined,
  map: ArtifactMap
): void {
  for (const nested of nestedArtifacts ?? []) {
    if (getArtifactType(nested) !== ArtifactType.FILE_ARTIFACT) continue;
    const name = getArtifactHeaderName(nested);
    if (!name) continue;
    const fileType = nested.file_artifact?.file_type;
    if (fileType === FileType.CHART_SPEC_JSON) {
      getOrCreate(map, ARTIFACT_TAG_TYPE.chart).set(name, parent);
    } else if (fileType === FileType.DOCX) {
      getOrCreate(map, ARTIFACT_TAG_TYPE.report).set(name, parent);
    } else {
      getOrCreate(map, ARTIFACT_TAG_TYPE.dataframe).set(name, parent);
    }
  }
}

function indexWorkflowRunAsParent(parent: ArtifactProto, map: ArtifactMap): void {
  const run = parent.workflow_run;
  indexWorkflowRunNestedNames(parent, run?.input_artifacts, map);
  indexWorkflowRunNestedNames(parent, run?.output_artifacts, map);
}

function indexArtifact(artifact: ArtifactProto, map: ArtifactMap): void {
  const name = getArtifactHeaderName(artifact);
  const type = getArtifactType(artifact);

  if (type === ArtifactType.DATAFRAME_ANALYSIS) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.code).set(name, artifact);
    artifact.dataframe_analysis?.pandas_dataframes.forEach(df => {
      if (df.name) getOrCreate(map, ARTIFACT_TAG_TYPE.dataframe).set(df.name, artifact);
    });
    artifact.dataframe_analysis?.charts.forEach(chart => {
      if (chart.name) getOrCreate(map, ARTIFACT_TAG_TYPE.chart).set(chart.name, artifact);
    });
    artifact.dataframe_analysis?.reports.forEach(report => {
      if (report.name) getOrCreate(map, ARTIFACT_TAG_TYPE.report).set(report.name, artifact);
    });
  } else if (type === ArtifactType.KG_DATAFRAME) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.dataframe).set(name, artifact);
  } else if (type === ArtifactType.SCENARIO_JOB) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.workflow).set(name, artifact);
  } else if (type === ArtifactType.ANALYSIS_OUTPUT) {
    if (name) getOrCreate(map, ARTIFACT_TAG_TYPE.workflowRun).set(name, artifact);
  } else if (type === ArtifactType.FILE_ARTIFACT) {
    indexFileArtifact(artifact, map);
  } else if (type === ArtifactType.WORKFLOW_RUN) {
    const key = getArtifactMapKey(artifact);
    if (key) getOrCreate(map, ARTIFACT_TAG_TYPE.workflowRunDetail).set(key, artifact);
    indexWorkflowRunAsParent(artifact, map);
  } else if (type === ArtifactType.WORKFLOW_RUN_SEARCH) {
    const key = getArtifactMapKey(artifact);
    if (key) getOrCreate(map, ARTIFACT_TAG_TYPE.workflowRunSearch).set(key, artifact);
    for (const run of artifact.workflow_run_search?.workflow_runs ?? []) {
      const runParent = ArtifactProto.create({
        artifact_type: ArtifactType.WORKFLOW_RUN,
        workflow_run: run,
      });
      indexWorkflowRunAsParent(runParent, map);
    }
  }
}

/** Find a FILE_ARTIFACT in the file bucket by header.name (bucket keys are header ids). */
function findFileArtifactByHeaderName(map: ArtifactMap, name: string): ArtifactProto | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const fileBucket = map.get(ARTIFACT_TAG_TYPE.file);
  if (!fileBucket) return undefined;
  return Array.from(fileBucket.values()).find(a => a.file_artifact?.header?.name?.trim() === trimmed);
}

/**
 * Returns the primary type-bucket key for a given artifact type.
 * Used by updateArtifact to check whether the artifact is registered before re-indexing.
 */
function primaryBucket(artifact: ArtifactProto): ArtifactKind {
  const type = getArtifactType(artifact);
  if (type === ArtifactType.DATAFRAME_ANALYSIS) return ARTIFACT_TAG_TYPE.code;
  if (type === ArtifactType.KG_DATAFRAME) return ARTIFACT_TAG_TYPE.dataframe;
  if (type === ArtifactType.SCENARIO_JOB) return ARTIFACT_TAG_TYPE.workflow;
  if (type === ArtifactType.FILE_ARTIFACT) {
    const fileType = artifact.file_artifact?.file_type;
    if (fileType === FileType.CHART_SPEC_JSON) return ARTIFACT_TAG_TYPE.chart;
    if (fileType === FileType.DOCX) return ARTIFACT_TAG_TYPE.report;
    return ARTIFACT_TAG_TYPE.file;
  }
  if (type === ArtifactType.WORKFLOW_RUN) return ARTIFACT_TAG_TYPE.workflowRunDetail;
  if (type === ArtifactType.WORKFLOW_RUN_SEARCH) return ARTIFACT_TAG_TYPE.workflowRunSearch;
  return ARTIFACT_TAG_TYPE.workflowRun;
}

/**
 * Nested artifact registry — single source of truth for the chat session.
 *
 * `artifactMap` is `Map<ArtifactKind, Map<name, ArtifactProto>>`, mirroring
 * the backend's `map<artifact_type, map<artifact_name, Artifact>>`.  Each type
 * bucket has its own inner map so artifacts of different types can share the same
 * name without collision (e.g. a "code" artifact and a "dataframe" artifact both
 * named "results" coexist independently).
 *
 * `lookupByName` still provides O(n_buckets) lookup — at most 6 Map.get calls —
 * which is effectively O(1) for practical session sizes.
 */
export function useArtifactStore() {
  const [artifactMap, setArtifactMap] = useState<ArtifactMap>(new Map());
  // Always points to the latest map so lookupByName doesn't need artifactMap as a dep.
  const artifactMapRef = useRef(artifactMap);
  artifactMapRef.current = artifactMap;

  /**
   * Register one or more artifacts. Each artifact is indexed into its type bucket.
   * Always overwrites — each call delivers a newer version of the artifact (e.g. a
   * later streaming chunk or a fresh agent run).
   * Deps: [] — helpers are module-level; only the stable React setter is used.
   */
  const registerArtifacts = useCallback((...artifacts: ArtifactProto[]) => {
    setArtifactMap(prev => {
      const next = cloneArtifactMap(prev);
      for (const artifact of artifacts) {
        indexArtifact(artifact, next);
      }
      return next;
    });
  }, []);

  /**
   * Replace an artifact in the map (used after populate()).
   * Re-indexes all sub-resource names to point to the updated artifact.
   * Deps: [] — helpers are module-level; only the stable React setter is used.
   */
  const updateArtifact = useCallback((next: ArtifactProto) => {
    const key = getArtifactMapKey(next);
    setArtifactMap(prev => {
      const bucket = primaryBucket(next);
      const isRegistered = prev.get(bucket)?.has(key);
      // Allow WORKFLOW_RUN populate to register nested name→parent mappings even when the
      // run was opened directly from a search-card populate (not yet in the store).
      if (!isRegistered && getArtifactType(next) !== ArtifactType.WORKFLOW_RUN) return prev;
      const updated = cloneArtifactMap(prev);
      indexArtifact(next, updated);
      return updated;
    });
  }, []);

  /**
   * Look up any artifact or sub-resource by name.
   * When `kind` is provided, searches only that type bucket (O(1)).
   * Without `kind`, searches all buckets sequentially (at most 6 Map.get calls).
   * Returns the ArtifactProto — for sub-resources this is the parent artifact.
   *
   * Deps: [] — reads via artifactMapRef so the reference never changes, preventing
   * useMemo invalidation in every MarkdownRenderer on each artifact registration.
   */
  const lookupByName = useCallback((name: string, kind?: ArtifactKind): ArtifactProto | undefined => {
    const trimmed = name.trim();
    if (kind) {
      const bucket = artifactMapRef.current.get(kind);
      const direct = bucket?.get(name);
      if (direct) return direct;
      // FILE_ARTIFACT bucket keys are header ids; parent_headers / [[dataframe:name]] use header.name.
      if (kind === ARTIFACT_TAG_TYPE.dataframe || kind === ARTIFACT_TAG_TYPE.file) {
        return findFileArtifactByHeaderName(artifactMapRef.current, name);
      }
      return undefined;
    }
    const buckets = Array.from(artifactMapRef.current.values());
    for (let i = 0; i < buckets.length; i++) {
      const found = buckets[i].get(name);
      if (found) return found;
    }
    return findFileArtifactByHeaderName(artifactMapRef.current, trimmed);
  }, []);

  /** Reset the store. */
  const clearStore = useCallback(() => {
    setArtifactMap(new Map());
  }, []);

  return {
    artifactMap,
    registerArtifacts,
    updateArtifact,
    lookupByName,
    clearStore,
  };
}
